import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, CircularProgress, Link, List, ListItem } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AutocompleteElement, DateTimePickerElement, FormContainer, TextFieldElement } from 'react-hook-form-mui';

import AutocompleteMultipleElement from '@components/common/HookFormMui/AutocompleteMultipleElement';
import CheckboxButtonGroup from '@components/common/HookFormMui/CheckboxButtonGroup';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import RequiredFields from '@components/common/HookFormMui/RequiredFields';
import useErrorHandling from '@components/common/hooks/useErrorHandling';
import useStaffUserLookup from '@components/common/hooks/useStaffUserLookup';
import useBoothTopicsQuery from '@components/common/hooks/api/useBoothTopicsQuery';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import moment from '@lib/moment';
import { toUserOption } from '@lib/user';
import { toSiteOption } from '@lib/utils';
import { toLessonOption } from '../eventFunctions';


const SessionForm = ({
  form,
  session,
  event,
  submitting,
  onSubmit,
  valuesSetState,
}) => {

  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [valuesSet, setValuesSet] = valuesSetState;

  const pickerOpenStart = useRef(false);

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = form;

  const deliveryMethodWatch = watch('delivery_method');
  const demoWatch = watch('demonstration');
  const lessonWatch = watch('lesson');

  useErrorHandling({ errors, isSubmitting, wasSubmitting, setWasSubmitting });
  const boothQuery = useBoothTopicsQuery(session);
  const { data: staffUsers, ...staffUsersQuery } = useStaffUsersQuery();
  const { mapCollaboratorsToOptions } = useStaffUserLookup();
  const pearsQuery = usePearsQuery();

  const languages = pearsQuery.data?.languageLabels;

  useEffect(() => {
    if (!staffUsers || !pearsQuery.data || valuesSet) {
      return;
    }
    if (session === false) {
      // No session means we are creating one
      if (event.event_type === 'BOOTH') {
        setValue('delivery_method', ['In Person']);
      }
      setValuesSet(true);
      return;
    } else if (!session) {
      console.log('session not found');
      // session was not found
      return;
    }
    const getLang = (langId) => {
      const lang = pearsQuery.data.languages.find((l) => (l.id === langId || l.label === langId));
      return lang?.label || langId;
    };
    const values = {
      session_name: session.session_name,
      booth_topic_name: session.booth_topic_name,
      event_id: session.event_id,
      lesson_id: session.lesson_id,
      lesson: session.lesson_id ? toLessonOption(session.lesson) : null,
      curriculum_id: session.curriculum_id,
      curriculum: session.curriculum_id ? toLessonOption(session.curriculum) : null,
      start: moment(session.start),
      end: moment(session.end),
      delivery_method: (event.event_type === 'BOOTH' ? ['In Person'] : session.delivery_method),
      collaborators: mapCollaboratorsToOptions(session.collaborators),
      delivery_language: session.delivery_language.map(getLang),
      material_language: session.material_language.map(getLang),
      delivery_location: toSiteOption(session.delivery_location_id, session.delivery_location),
      delivery_location_id: session.delivery_location_id,
      demonstration: session.demonstration ? 'true' : 'false',
      recipes: session.recipes,
      code: session.code,
    };
    Object.keys(values).forEach((key) => setValue(key, values[key]));
    setValuesSet(true);
  }, [event.event_type, pearsQuery.data, session, setValue, setValuesSet, staffUsers, valuesSet]);

  const staffUserOptions = useMemo(() => {
    if (!staffUsers) return [];
    return staffUsers.filter(u => u.email !== event.created_by).map(toUserOption);
  }, [staffUsers]);

  const handleDeliveryMethodChange = (value) => {
    if (!value.includes('In Person')) {
      setValue('delivery_location', null);
    }
  }
  const handleDemoChange = (value) => {
    if (value !== 'true') {
      setValue('recipes', null);
    }
  };

  const handleStartChange = (value) => {
    if (!pickerOpenStart.current) {
      handleStartAccept(value);
    } else {
      if (!value) return true;
      trigger('start').then();
      trigger('end').then();
    }
  };

  const handleStartAccept = (value) => {
    if (!value) return true;
    const eventEnd = getValues('end');
    let newEnd = (eventEnd && eventEnd.isValid()) ? eventEnd.clone() : null;
    if (!eventEnd && value && value.isValid()) {
      newEnd = value.clone();
      newEnd.add(1, 'hours');
      setValue('end', newEnd);
    } else if (newEnd && value && value.isValid()) {
      newEnd.set({
        year: value.get('year'),
        month: value.get('month'),
        date: value.get('date'),
      }).format();
      setValue('end', newEnd);
    }
    trigger('start').then();
    trigger('end').then();
  };

  const getFormField = (field) => (
    <ListItem sx={{ display: "list-item" }} key={`field-${field.props.id}`}>
      { field }
    </ListItem>
  );

  if (boothQuery.isLoading || staffUsersQuery.isLoading || pearsQuery.isLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  const fields = [];

  if (event?.event_type === 'CLASS' && !event?.curriculum?.custom) {
    // Class events have a curriculum set at the event level. Show lessons from that curriculum.
    // The exception is for "custom" curricula, where the lesson selector does not appear.
    // For those events, the user enters a custom lesson name into the "session_name" field.
    const options = event?.curriculum?.lessons?.map((c) => ({
      id: c.id,
      label: c.lesson_name,
      audience: c.audience,
    })) || [];
    fields.push(
      <AutocompleteElement
        id="field-lesson"
        name="lesson"
        label={'What lesson will you be teaching?'}
        placeholder={'Choose curriculum'}
        control={control}
        fullWidth
        required
        options={options}
        autocompleteProps={{
          isOptionEqualToValue: (option, newValue) => {
            return option?.id === newValue?.id;
          }
        }}
      />
    );
  } else if (event.event_type === 'BOOTH') {
    // Booth events don't have a curriculum, so we list all booth topics that are valid for this event.
    const options = boothQuery.data.map((c) => ({
      id: c.id,
      label: c.booth_topic,
      audience: c.audience,
      custom: c.custom,
    }));
    fields.push(
      <AutocompleteElement
        id="field-lesson"
        name="lesson"
        label={'Which booth topic will you be doing?'}
        placeholder={'Choose curriculum'}
        control={control}
        fullWidth
        required
        options={options}
        autocompleteProps={{
          isOptionEqualToValue: (option, newValue) => {
            return option?.id === newValue?.id;
          },
          onChange: (_, value) => {
            if (!value?.custom) {
              setValue('booth_topic_name', null);
            }
          },
        }}
      />
    );

    if (lessonWatch?.custom) {
      fields.push(
        <TextFieldElement
          id="field-booth-topic-name"
          name="booth_topic_name"
          label="Please describe"
          required
          validation={{ required: 'This field is required.' }}
          control={control}
          fullWidth
        />
      );
    }
  }

  // Session name.
  // For events with custom curricula, this is the lesson name.
  // For events with non-custom curricula, this text is appended to the lesson name from the
  // lessons table to make the full session name.
  if (event.event_type === 'CLASS') {
    fields.push(
      <TextFieldElement
        id="field-session_name"
        name="session_name"
        label={event.curriculum?.custom ?
          'What lesson will you be teaching?' :
          "Add any additional lesson name descriptors, if needed."}
        required={event.curriculum?.custom}
        helperText={
          event.curriculum?.custom ? null :
        (
          <>Example: If you will deliver one lesson over two scheduled sessions, put &quot;Part 1&quot; for the first
            session and &quot;Part 2&quot; for the second session.</>
        )}
        control={control}
        fullWidth
      />
    );
  }

  fields.push(
    <DateTimePickerElement
      id="field-start"
      name="start"
      label={'What is the planned start date & time for this session?'}
      control={control}
      required
      validation={{
        validate: {
          /** @param {AdapterMoment} value **/
          range: (value) => {
            const endDate = getValues('end');
            if (!value || !endDate) return true;
            const valid = !value.isAfter(endDate);
            return valid || 'Start Date & Time must be less than End Date & Time';
          }
        }
      }}
      onOpen={() => { pickerOpenStart.current = true }}
      onClose={() => { pickerOpenStart.current = false }}
      onAccept={handleStartAccept}
      onChange={handleStartChange}
      inputProps={{
        fullWidth: true,
        id: 'field-start',
      }}
    />
  );

  fields.push(
    <DateTimePickerElement
      id="field-end"
      name="end"
      label={'What is the planned end date & time for this session'}
      helperText={'End Date & Time must be greater than Start Date & Time.'}
      control={control}
      required
      validation={{
        validate: {
          /** @param {AdapterMoment} value **/
          range: (value) => {
            const eventStart = getValues('start');
            if (!value || !eventStart) return true;
            const valid = value.isAfter(eventStart);
            return valid || 'End Date & Time must be greater than Start Date & Time';
          }
        }
      }}
      onChange={() => {
        trigger('start').then();
        trigger('end').then();
      }}
      inputProps={{
        fullWidth: true,
        id: 'field-end',
      }}
    />
  );

  fields.push(
    <CheckboxButtonGroup
      label="What method will be used to deliver this session? Select all that apply."
      id="field-delivery_method"
      name="delivery_method"
      control={control}
      required
      fullWidth
      readOnly={event.event_type === 'BOOTH'}
      onChange={handleDeliveryMethodChange}
      options={[
        'In Person',
        'Virtual/Live Online',
      ]}
    />
  );

  /**
   * PEARS field
   */
  if (deliveryMethodWatch?.includes('In Person') || event.event_type === 'BOOTH') {
    fields.push(
      <AutocompleteElement
        id="field-delivery_location"
        name="delivery_location"
        label="Where will this session be delivered?"
        textFieldProps={{
          helperText: <>If you cannot find a site, please email <Link href="mailto:uie-pears-notifications@illinois.edu">uie-pears-notifications@illinois.edu</Link> to request to add the site.</>
        }}
        control={control}
        fullWidth
        required
        options={pearsQuery.data?.sites || []}
        autocompleteProps={{
          isOptionEqualToValue: (option, newValue) => {
            return (option?.id ?? option) === (newValue?.id ?? newValue);
          }
        }}
      />
    );
  }

  fields.push(
    <AutocompleteMultipleElement
      id="field-delivery_language"
      name="delivery_language"
      label="What language(s) will this session be delivered in? Select all that apply."
      control={control}
      fullWidth
      required
      options={languages}
    />
  );

  fields.push(
    <AutocompleteMultipleElement
      id="field-material_language"
      name="material_language"
      label="What language(s) will participant materials be provided in? Select all that apply."
      control={control}
      fullWidth
      required
      options={languages}
    />
  );

  fields.push(
    <RadioButtonGroup
      id="field-demonstration"
      name="demonstration"
      label="Will this session include a food demonstration / taste test?"
      control={control}
      fullWidth
      required
      onChange={handleDemoChange}
      options={[
        { label: 'Yes', id: 'true' },
        { label: 'No', id: 'false' },
      ]}
    />
  );

  if (demoWatch === 'true') {
    fields.push(
      <TextFieldElement
        id="field-recipes"
        name="recipes"
        label="What recipe(s) do you plan to use?"
        control={control}
        required
        fullWidth
      />
    );
  }

  fields.push(
    <AutocompleteMultipleElement
      id="field-collaborators"
      name="collaborators"
      label="Select any additional users who are collaborating on this session."
      control={control}
      fullWidth
      options={staffUserOptions}
      autocompleteProps={{
        isOptionEqualToValue: (option, newValue) => {
          return option.id === newValue.id;
        }
      }}
    />
  );

  return (
    <Box width={'100%'}>
      { !!valuesSet && (
        <FormContainer onSuccess={handleSubmit(onSubmit)} FormProps={{ className: 'mui' }}>
          <RequiredFields />
          <Grid container columns={12} spacing={2}>
            <Grid xs={12} sm={10} md={8}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <List
                  sx={{
                    listStyle: 'decimal',
                    paddingLeft: 2,
                  }}
                  data-cy={'fields'}
                >
                  { fields.map(getFormField) }
                </List>
              </LocalizationProvider>
            </Grid>

            <Grid xs={12} sm={10} md={8}>
              <Button
                id="button-submit"
                disabled={submitting}
                type={'submit'}
                variant="contained"
                size="small"
                sx={{ marginTop: '20px' }}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </FormContainer>
      )}
    </Box>
  );
};

export default SessionForm;
